
import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';

interface ParticleProps {
  count?: number;
  color?: string;
}

export const ParticleBackground: React.FC<ParticleProps> = ({ count = 50, color = '#ffffff' }) => {
  const particles = useRef<JSX.Element[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Reduce particle count on mobile for better performance
    const particleCount = isMobile ? Math.floor(count / 2) : count;
    
    particles.current = Array.from({ length: particleCount }).map((_, i) => {
      const size = Math.random() * 3 + 1;
      const xPos = Math.random() * 100;
      const yPos = Math.random() * 100;
      const duration = Math.random() * 80 + 20;
      const delay = Math.random() * 5;
      const opacity = Math.random() * 0.3 + 0.1;

      return (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: size,
            height: size,
            backgroundColor: color,
            left: `${xPos}%`,
            top: `${yPos}%`,
            opacity: opacity,
            // Remove transform3d to avoid stacking context issues on mobile
            backfaceVisibility: 'hidden',
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [opacity, opacity * 1.5, opacity],
          }}
          transition={{
            duration: duration,
            repeat: Infinity,
            delay: delay,
            ease: 'easeInOut',
          }}
        />
      );
    });
  }, [count, color, isMobile]);

  return (
    <div 
      className="absolute inset-0 overflow-hidden pointer-events-none opacity-50"
      style={{
        // Ensure particles stay behind content
        zIndex: -1,
        // Disable on mobile if causing performance issues
        display: isMobile ? 'none' : 'block'
      }}
    >
      {particles.current}
    </div>
  );
};
